(() => {
  'use strict';

  if (!document.body.classList.contains('template-collection') || !('IntersectionObserver' in window)) return;

  const wrapperSelector = '[data-wrap-lm]';
  const buttonSelector = '[data-load-more]';
  const currentSelector = '[data-current-bar]';
  const productSelector = '.t4s-product, .t4s-product-item';
  const states = new WeakMap();
  const sentinelStates = new WeakMap();

  let observer;
  let mutationObserver;
  let refreshQueued = false;

  const toWesternDigits = (value) => String(value || '')
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

  const readCounts = (wrapper) => {
    const label = wrapper.querySelector('.t4s-lm-bar--txt');
    const currentNode = wrapper.querySelector(currentSelector);
    const numbers = toWesternDigits(label?.textContent).match(/\d+/g) || [];
    const current = Number(toWesternDigits(currentNode?.textContent).match(/\d+/)?.[0] || numbers[0] || 0);
    const total = Number(numbers[numbers.length - 1] || current || 0);

    return {
      current: Number.isFinite(current) ? current : 0,
      total: Number.isFinite(total) ? total : 0
    };
  };

  const readProductCount = () => document.querySelectorAll(productSelector).length;

  const setStatus = (state, mode = 'idle') => {
    if (!state.wrapper.isConnected) return;

    const counts = readCounts(state.wrapper);
    const button = state.wrapper.querySelector(buttonSelector);
    const hasMore = Boolean(button?.getAttribute('href'));

    if (hasMore) {
      state.lastHref = button.getAttribute('href');
      state.fallback.href = state.lastHref;
    }

    if (!hasMore && counts.total) {
      state.wrapper.classList.add('ts-auto-complete');
      state.count.textContent = `تم عرض جميع المنتجات — ${counts.total} منتجًا`;
      state.hint.textContent = '';
      state.fallback.removeAttribute('href');
      return;
    }

    state.wrapper.classList.remove('ts-auto-complete');
    state.count.textContent = counts.total
      ? `تم عرض ${counts.current} من ${counts.total} منتجًا`
      : `تم عرض ${counts.current} منتجًا`;

    if (mode === 'loading') {
      state.hint.textContent = 'جارٍ تحميل المزيد…';
    } else if (mode === 'failed') {
      state.hint.textContent = 'تعذّر التحميل التلقائي؛ يمكن المتابعة من الزر أدناه.';
    } else {
      state.hint.textContent = 'مرّري لمشاهدة المزيد';
    }
  };

  const clearTimer = (state) => {
    if (!state.timer) return;
    window.clearInterval(state.timer);
    state.timer = null;
  };

  const finishLoading = (state, succeeded) => {
    clearTimer(state);
    state.loading = false;
    state.wrapper.classList.remove('ts-auto-loading');
    state.wrapper.removeAttribute('aria-busy');

    if (succeeded) {
      state.wrapper.classList.remove('ts-auto-failed');
      setStatus(state, 'idle');

      observer.unobserve(state.sentinel);
      window.setTimeout(() => {
        if (state.wrapper.isConnected && state.wrapper.querySelector(buttonSelector)) {
          observer.observe(state.sentinel);
        }
      }, 1400);
    } else {
      state.wrapper.classList.add('ts-auto-failed');
      if (state.lastHref) state.fallback.href = state.lastHref;
      setStatus(state, 'failed');
    }
  };

  const monitorLoading = (state, before) => {
    const startedAt = Date.now();

    clearTimer(state);
    state.timer = window.setInterval(() => {
      if (!state.wrapper.isConnected) {
        clearTimer(state);
        return;
      }

      const button = state.wrapper.querySelector(buttonSelector);
      const currentHref = button?.getAttribute('href') || '';
      const counts = readCounts(state.wrapper);
      const productCount = readProductCount();
      const succeeded = !button
        || currentHref !== before.href
        || counts.current !== before.current
        || productCount > before.productCount;

      if (succeeded) {
        finishLoading(state, true);
      } else if (Date.now() - startedAt >= 9000) {
        finishLoading(state, false);
      }
    }, 180);
  };

  function loadNextPage(state) {
    if (!state.wrapper.isConnected || state.loading) return;

    const button = state.wrapper.querySelector(buttonSelector);
    const href = button?.getAttribute('href');
    if (!button || !href) {
      setStatus(state, 'idle');
      return;
    }

    const counts = readCounts(state.wrapper);
    const before = {
      href,
      current: counts.current,
      productCount: readProductCount()
    };

    state.loading = true;
    state.lastHref = href;
    state.wrapper.classList.remove('ts-auto-failed');
    state.wrapper.classList.add('ts-auto-loading');
    state.wrapper.setAttribute('aria-busy', 'true');
    state.fallback.href = href;
    setStatus(state, 'loading');

    button.click();
    monitorLoading(state, before);
  }

  const enhanceWrapper = (wrapper) => {
    const existing = states.get(wrapper);
    if (existing?.sentinel.isConnected && existing?.count.isConnected) {
      setStatus(existing, existing.loading ? 'loading' : 'idle');
      return;
    }

    if (existing) {
      clearTimer(existing);
      observer.unobserve(existing.sentinel);
      sentinelStates.delete(existing.sentinel);
      states.delete(wrapper);
    }

    const nativeButton = wrapper.querySelector(buttonSelector);
    if (!nativeButton) return;

    wrapper.classList.add('ts-auto-enhanced');

    const count = document.createElement('div');
    count.className = 'ts-auto-count';
    count.setAttribute('role', 'status');
    count.setAttribute('aria-live', 'polite');

    const hint = document.createElement('div');
    hint.className = 'ts-auto-hint';

    const fallback = document.createElement('a');
    fallback.className = 'ts-auto-fallback';
    fallback.textContent = 'تحميل المزيد من المنتجات';

    const sentinel = document.createElement('div');
    sentinel.className = 'ts-auto-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');

    const bar = wrapper.querySelector('[data-wrap-lm-bar]');
    if (bar) bar.append(count);
    else wrapper.prepend(count);

    wrapper.append(hint, fallback, sentinel);

    const state = {
      wrapper,
      count,
      hint,
      fallback,
      sentinel,
      lastHref: nativeButton.getAttribute('href') || '',
      loading: false,
      timer: null
    };

    states.set(wrapper, state);
    sentinelStates.set(sentinel, state);
    observer.observe(sentinel);
    setStatus(state, 'idle');
  };

  const refreshWrappers = () => {
    refreshQueued = false;
    document.querySelectorAll(wrapperSelector).forEach(enhanceWrapper);
  };

  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(refreshWrappers);
  };

  const start = () => {
    document.documentElement.classList.add('ts-auto-load-enabled');

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const state = sentinelStates.get(entry.target);
        if (state) loadNextPage(state);
      });
    }, {
      root: null,
      rootMargin: '0px 0px 500px 0px',
      threshold: 0.01
    });

    refreshWrappers();

    mutationObserver = new MutationObserver(queueRefresh);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const startAfterTheme = () => window.setTimeout(start, 900);

  if (document.readyState === 'complete') startAfterTheme();
  else window.addEventListener('load', startAfterTheme, { once: true });
})();
